// WorkspaceHeader.tsx — one workspace's row in the sessions sidebar, above
// the sessions that run on it.
//
// A workspace is the machine: its Pod, its working tree, what was
// installed on it. The header says whether that machine is up, names it
// (editable in place), and carries the things that act on the machine
// rather than on a conversation: starting another session on it, deleting
// it with everything it holds, and — last, an IDE icon — opening its Code
// OSS in a new browser tab.

import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { InlineText } from 'common';
import { BsCodeSquare, BsHddStack, BsPlusCircle, BsTrash, BsX } from 'react-icons/bs';
import { workspacesApi } from '../api';
import { getColorScheme } from '../common/theme';
import type { Workspace } from '../api';
import { OPEN_IN_VS_CODE_TITLE, useCodeLaunch } from './codelaunch/OpenInVsCodeButton';

interface Props {
  workspace: Workspace;
  /** Live phase from the manager socket; undefined until the first frame. */
  phase?: string;
  /** The row has not arrived yet: render the name, hold the controls. */
  placeholder: boolean;
  sessionCount: number;
  /** A session is being created in this workspace. */
  creating?: boolean;
  onRename: (name: string) => void;
  onNewSession: () => void;
  onDelete: () => void;
}

/** A short word for the Pod's state, and the color it is drawn in. */
function phaseLabel(phase?: string): { text: string; className: string } {
  switch (phase) {
    case 'ready':
      return { text: 'running', className: 'text-success' };
    case 'suspended':
      return { text: 'suspended', className: 'text-warning' };
    case 'provisioning':
      return { text: 'starting', className: 'text-info' };
    default:
      return { text: '', className: 'text-muted' };
  }
}

export const WorkspaceHeader: React.FC<Props> = ({
  workspace, phase, placeholder, sessionCount, creating, onRename, onNewSession, onDelete,
}) => {
  const [draft, setDraft] = useState<string | undefined>(undefined);
  // The last "Open in VS Code" failure, shown under the button until
  // dismissed or the next attempt.
  const [launchError, setLaunchError] = useState('');
  const label = phaseLabel(phase);
  const code = useCodeLaunch({
    workspaceId: workspace.id,
    launch: (id) => workspacesApi.codeLaunch(id, getColorScheme()),
    onStart: () => setLaunchError(''),
    onError: setLaunchError,
  });

  // Commit on blur/Enter; a no-op when nothing changed, so tabbing
  // through the header does not issue a rename.
  const commit = () => {
    const next = draft?.trim();
    setDraft(undefined);
    if (next && next !== workspace.name) onRename(next);
  };

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: 0 }}>
      <div
        className="d-flex align-items-center gap-1 small text-muted"
        style={{ minWidth: 0 }}
        title={`${sessionCount} session${sessionCount === 1 ? '' : 's'} share this workspace's machine and files`}
      >
        <BsHddStack style={{ flexShrink: 0 }} aria-hidden />
        <InlineText
          value={draft ?? workspace.name}
          minWidth={8}
          aria-label="Workspace name"
          disabled={placeholder}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setDraft(undefined);
              e.currentTarget.blur();
            }
          }}
          onBlur={commit}
          style={{
            color: 'inherit',
            fontWeight: 600,
            fontSize: 'inherit',
            flexGrow: 1,
            minWidth: 0,
            maxWidth: '100%',
            borderColor: 'transparent',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
          title={workspace.name}
        />
        {phase === 'provisioning' ? (
          <Spinner animation="border" size="sm" style={{ flexShrink: 0, width: '0.7rem', height: '0.7rem' }} />
        ) : null}
        {label.text && (
          <span className={label.className} style={{ flexShrink: 0, fontSize: '0.7rem' }}>
            ● {label.text}
          </span>
        )}
        <Button
          size="sm"
          variant="link"
          className="text-muted p-1"
          onClick={onNewSession}
          disabled={placeholder || creating}
          title="New session in this workspace"
          aria-label="New session in this workspace"
        >
          {creating ? <Spinner animation="border" size="sm" style={{ width: '0.8rem', height: '0.8rem' }} /> : <BsPlusCircle />}
        </Button>
        <Button
          size="sm"
          variant="link"
          className="text-muted p-1"
          onClick={onDelete}
          disabled={placeholder}
          title="Delete workspace"
          aria-label="Delete workspace"
        >
          <BsTrash />
        </Button>
        <Button
          size="sm"
          variant="link"
          className="text-muted p-1"
          onClick={code.onClick}
          disabled={placeholder || code.pending}
          aria-busy={code.pending}
          aria-label="Open in VS Code"
          title={OPEN_IN_VS_CODE_TITLE}
        >
          {code.pending
            ? <Spinner animation="border" size="sm" style={{ width: '0.8rem', height: '0.8rem' }} />
            : <BsCodeSquare />}
        </Button>
      </div>
      {launchError && (
        <div className="d-flex align-items-start gap-1 small text-danger" role="alert">
          <span className="text-break">Could not open VS Code: {launchError}</span>
          <Button
            size="sm"
            variant="link"
            className="text-danger p-0"
            onClick={() => setLaunchError('')}
            title="Dismiss"
            aria-label="Dismiss"
          >
            <BsX />
          </Button>
        </div>
      )}
    </div>
  );
};

export default WorkspaceHeader;
